// GA4 + GTM loader, gated on cookie consent AND production hostname.
// Scripts are injected only when the user has explicitly accepted "analytics"
// AND the site is running on the real production domain. Preview / localhost
// never fire tags, keeping the analytics stream clean.

import { siteConfig } from "@/config/siteConfig";

const PRODUCTION_HOSTS = ["axion-grp.com", "www.axion-grp.com"];

let injected = false;

export function isProductionHost() {
  if (typeof window === "undefined") return false;
  return PRODUCTION_HOSTS.includes(window.location.hostname);
}

export function injectAnalytics() {
  if (injected) return;
  if (!isProductionHost()) return; // never load on preview / localhost
  const { GA_MEASUREMENT_ID, GTM_ID } = siteConfig;
  if (!GA_MEASUREMENT_ID && !GTM_ID) return;

  if (GA_MEASUREMENT_ID) {
    const s1 = document.createElement("script");
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js', new Date());gtag('config', '${GA_MEASUREMENT_ID}');`;
    document.head.appendChild(s2);
  }

  if (GTM_ID) {
    const s = document.createElement("script");
    s.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_ID}');`;
    document.head.appendChild(s);
  }

  injected = true;
}

// ---------- Event tracking ----------

/**
 * Fire a custom GA4 event. No-ops on non-production, when the user has not
 * consented, or when gtag hasn't loaded. Always safe to call.
 */
export function trackEvent(name, params = {}) {
  try {
    if (typeof window === "undefined") return;
    if (!isProductionHost()) return;
    const gtag = window.gtag;
    if (typeof gtag !== "function") return;
    gtag("event", name, params);
  } catch {
    /* analytics failures must never break the app */
  }
}

/** Fired on any successful form submission. lead_type ∈ sample_request | distributor | technical | general */
export function trackLeadSubmit(leadType) {
  trackEvent("lead_submit", { lead_type: leadType });
}

/** Fired on product detail page load. */
export function trackProductView(productSlug) {
  trackEvent("product_view", { product_slug: productSlug });
}

/** Fired when a primary CTA is clicked. cta_source ∈ header | hero | footer | product_page | ... */
export function trackCtaClick(ctaSource, ctaLabel) {
  trackEvent("cta_click", { cta_source: ctaSource, cta_label: ctaLabel });
}

// ---------- Consent storage ----------
const CONSENT_KEY = "cookie-consent-v1";

export function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeConsent(choices) {
  const payload = {
    ts: new Date().toISOString(),
    choices,
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  if (choices.analytics) injectAnalytics();
}
